
import { GenericGrid } from '@amsanalytics/react-shared-library';
import React from 'react';
import styles from './_lease-deferred-down-editor.module.scss';
import { hasValue } from "@amsanalytics/react-shared-library";
import { useDispatch } from "react-redux";
import produce from "immer";
import { Card, CardHeader, CardTitle } from '@progress/kendo-react-layout';
import {IApplication} from "../../../../Interfaces/Application/IApplication";
import {ApplicationDispatch} from "../../../../store/application/types";
import {useDialog} from "../../../../providers/DialogProvider";
import {useBusinessRulesEngine} from "../../../../Data/BusinessRulesData";
import {useConfiguration} from "../../../../Data/ConfigurationData";
import {updateEditingApp} from "../../../../store/application/actions";
import GenericItemEditorDialog, {
  GenericEditorActionType,
  IGenericItemEditorError,
  IGenericItemFieldInfo
} from "../../GenericItemEditorDialog/GenericItemEditorDialog";
import {IApplicationLeaseQuote} from "../../../../Interfaces/Application/IApplicationLeaseQuote";
import {startOfDay} from "../../../../utils/LoanCalcUtils";

interface ILeaseQuoterDeferredDownForGenericEditor {
  id: string; // really a fake ID just to differentiate them
  paymentNumber: number;
  paymentDate: Date | undefined;
  amount: number;
}

export interface LeaseDeferredDownEditorProps {
  leaseQuoteFieldIsDisabled: (fieldName: string) => boolean;
  currentApp: IApplication;
  className?: string;
}

export default function LeaseDeferredDownEditor(props: LeaseDeferredDownEditorProps) {
  const applicationLeaseQuote = props.currentApp.applicationLeaseQuote;
  const dispatch = useDispatch<ApplicationDispatch>();
  const [openDialog, closeDialog] = useDialog();
  const businessRulesEngine = useBusinessRulesEngine();
  const { data: configuration } = useConfiguration();

  const contractDateMin = startOfDay(new Date(applicationLeaseQuote.contractDate!));

  const editableFieldInfos: IGenericItemFieldInfo[] = [
    { name: "paymentDate", label: "Payment Date", type: "Date", required: true, minDate: contractDateMin },
    { name: "amount", label: "Amount", type: "number", required: true }
  ];

  const maxNumberOfPayments = configuration?.deferredDownMaxPayments ?? 6;

  const deferredPayments: ILeaseQuoterDeferredDownForGenericEditor[] = [
    {id: '1', paymentNumber: 1, paymentDate: applicationLeaseQuote.deferredPmtDate1, amount: applicationLeaseQuote.deferredPmtAmount1 ?? 0},
    {id: '2', paymentNumber: 2, paymentDate: applicationLeaseQuote.deferredPmtDate2, amount: applicationLeaseQuote.deferredPmtAmount2 ?? 0},
    {id: '3', paymentNumber: 3, paymentDate: applicationLeaseQuote.deferredPmtDate3, amount: applicationLeaseQuote.deferredPmtAmount3 ?? 0},
    {id: '4', paymentNumber: 4, paymentDate: applicationLeaseQuote.deferredPmtDate4, amount: applicationLeaseQuote.deferredPmtAmount4 ?? 0},
    {id: '5', paymentNumber: 5, paymentDate: applicationLeaseQuote.deferredPmtDate5, amount: applicationLeaseQuote.deferredPmtAmount5 ?? 0},
    {id: '6', paymentNumber: 6, paymentDate: applicationLeaseQuote.deferredPmtDate6, amount: applicationLeaseQuote.deferredPmtAmount6 ?? 0}
  ];

  // For the old deferred down payment dialog
  const validateDeferredDownPayment = (deferredDownForGenericEditor: ILeaseQuoterDeferredDownForGenericEditor, actionType: GenericEditorActionType): IGenericItemEditorError[] => {
    const errors: IGenericItemEditorError[] = [];
    deferredDownForGenericEditor.paymentDate?.setHours(0, 0, 0, 0);
    
    if (!hasValue(deferredDownForGenericEditor.paymentDate)) {
      errors.push({ message: "Please enter the payment date", canBeBypassed: false });
    }

    if (hasValue(deferredDownForGenericEditor.paymentDate) && deferredDownForGenericEditor.amount <= 0) {
      errors.push({ message: "The payment amount has to be greater than 0", canBeBypassed: false });
    }
    
    const deferredPaymentDates = deferredPayments.filter(dp => dp.paymentDate !== undefined).map(dp => new Date(dp.paymentDate!));
    const numberOfExistingPaymentDatesWithSameDate = deferredPaymentDates.filter(dpd => dpd.setHours(0, 0, 0, 0) === deferredDownForGenericEditor.paymentDate?.setHours(0, 0, 0, 0)).length;

    if (numberOfExistingPaymentDatesWithSameDate > 0) { // There is already one for that date because you're updating it
      errors.push({ message: "The payment date specified is already in use", canBeBypassed: false });
    }

    return errors;
  }

  const handleRemoveSingleDeferredPayment = (deferredPaymentId: string) => {
    // Using this to match on the date for the other quotes
    const amountFieldName = `deferredPmtAmount${deferredPaymentId}`;
    const dateFieldName = `deferredPmtDate${deferredPaymentId}`;

    const updatedApp = produce(props.currentApp, updatedCurrentApp => {
      updatedCurrentApp.applicationLeaseQuote[amountFieldName] = undefined;
      updatedCurrentApp.applicationLeaseQuote[dateFieldName] = undefined;

      consolidateDeferredDownPayments(updatedCurrentApp.applicationLeaseQuote);
    });
    dispatch(updateEditingApp(updatedApp, businessRulesEngine));

    closeDialog();
  };
  
  // Sorts and takes out any gaps in the deferred payments because they are hardcoded
  const consolidateDeferredDownPayments = (applicationLeaseQuoteToUpdate: IApplicationLeaseQuote): void => {
    const deferredDownPaymentPairs: {paymentDate: Date | undefined, amount: number | undefined}[] = [
      {paymentDate: applicationLeaseQuoteToUpdate.deferredPmtDate1, amount: applicationLeaseQuoteToUpdate.deferredPmtAmount1},
      {paymentDate: applicationLeaseQuoteToUpdate.deferredPmtDate2, amount: applicationLeaseQuoteToUpdate.deferredPmtAmount2},
      {paymentDate: applicationLeaseQuoteToUpdate.deferredPmtDate3, amount: applicationLeaseQuoteToUpdate.deferredPmtAmount3},
      {paymentDate: applicationLeaseQuoteToUpdate.deferredPmtDate4, amount: applicationLeaseQuoteToUpdate.deferredPmtAmount4},
      {paymentDate: applicationLeaseQuoteToUpdate.deferredPmtDate5, amount: applicationLeaseQuoteToUpdate.deferredPmtAmount5},
      {paymentDate: applicationLeaseQuoteToUpdate.deferredPmtDate6, amount: applicationLeaseQuoteToUpdate.deferredPmtAmount6}
    ];
    
    const sortedDeferredDownPaymentPairs = deferredDownPaymentPairs
      .filter(dp => hasValue(dp.paymentDate) && hasValue(dp.amount))
      .sort((a, b) => new Date(a.paymentDate!).getTime() - new Date(b.paymentDate!).getTime());

    applicationLeaseQuoteToUpdate.deferredPmtDate1 = undefined;
    applicationLeaseQuoteToUpdate.deferredPmtAmount1 = undefined;
    applicationLeaseQuoteToUpdate.deferredPmtDate2 = undefined;
    applicationLeaseQuoteToUpdate.deferredPmtAmount2 = undefined;
    applicationLeaseQuoteToUpdate.deferredPmtDate3 = undefined;
    applicationLeaseQuoteToUpdate.deferredPmtAmount3 = undefined;
    applicationLeaseQuoteToUpdate.deferredPmtDate4 = undefined;
    applicationLeaseQuoteToUpdate.deferredPmtAmount4 = undefined;
    applicationLeaseQuoteToUpdate.deferredPmtDate5 = undefined;
    applicationLeaseQuoteToUpdate.deferredPmtAmount5 = undefined;
    applicationLeaseQuoteToUpdate.deferredPmtDate6 = undefined;
    applicationLeaseQuoteToUpdate.deferredPmtAmount6 = undefined;
    
    if (sortedDeferredDownPaymentPairs.length > 0) {
      applicationLeaseQuoteToUpdate.deferredPmtDate1 = sortedDeferredDownPaymentPairs[0].paymentDate;
      applicationLeaseQuoteToUpdate.deferredPmtAmount1 = sortedDeferredDownPaymentPairs[0].amount;
    }
    if (sortedDeferredDownPaymentPairs.length > 1) {
      applicationLeaseQuoteToUpdate.deferredPmtDate2 = sortedDeferredDownPaymentPairs[1].paymentDate;
      applicationLeaseQuoteToUpdate.deferredPmtAmount2 = sortedDeferredDownPaymentPairs[1].amount;
    }
    if (sortedDeferredDownPaymentPairs.length > 2) {
      applicationLeaseQuoteToUpdate.deferredPmtDate3 = sortedDeferredDownPaymentPairs[2].paymentDate;
      applicationLeaseQuoteToUpdate.deferredPmtAmount3 = sortedDeferredDownPaymentPairs[2].amount;
    }
    if (sortedDeferredDownPaymentPairs.length > 3) {
      applicationLeaseQuoteToUpdate.deferredPmtDate4 = sortedDeferredDownPaymentPairs[3].paymentDate;
      applicationLeaseQuoteToUpdate.deferredPmtAmount4 = sortedDeferredDownPaymentPairs[3].amount;
    }
    if (sortedDeferredDownPaymentPairs.length > 4) {
      applicationLeaseQuoteToUpdate.deferredPmtDate5 = sortedDeferredDownPaymentPairs[4].paymentDate;
      applicationLeaseQuoteToUpdate.deferredPmtAmount5 = sortedDeferredDownPaymentPairs[4].amount;
    }
    if (sortedDeferredDownPaymentPairs.length > 5) {
      applicationLeaseQuoteToUpdate.deferredPmtDate6 = sortedDeferredDownPaymentPairs[5].paymentDate;
      applicationLeaseQuoteToUpdate.deferredPmtAmount6 = sortedDeferredDownPaymentPairs[5].amount;
    }
  }

  const handleRemoveAllDeferredPayments = () => {
    const updatedApp = produce(props.currentApp, updatedCurrentApp => {
      const applicationLeaseQuote = updatedCurrentApp.applicationLeaseQuote;

      applicationLeaseQuote.deferredPmtAmount1 = undefined;
      applicationLeaseQuote.deferredPmtAmount2 = undefined;
      applicationLeaseQuote.deferredPmtAmount3 = undefined;
      applicationLeaseQuote.deferredPmtAmount4 = undefined;
      applicationLeaseQuote.deferredPmtAmount5 = undefined;
      applicationLeaseQuote.deferredPmtAmount6 = undefined;
      
      applicationLeaseQuote.deferredPmtDate1 = undefined;
      applicationLeaseQuote.deferredPmtDate2 = undefined;
      applicationLeaseQuote.deferredPmtDate3 = undefined;
      applicationLeaseQuote.deferredPmtDate4 = undefined;
      applicationLeaseQuote.deferredPmtDate5 = undefined;
      applicationLeaseQuote.deferredPmtDate6 = undefined;
    });
    dispatch(updateEditingApp(updatedApp, businessRulesEngine));

    closeDialog();
  }

  const handleUpdateDeferredPayment = (deferredDownForGenericEditor: ILeaseQuoterDeferredDownForGenericEditor): void => {
    const amountFieldName = `deferredPmtAmount${deferredDownForGenericEditor.paymentNumber}`;
    const dateFieldName = `deferredPmtDate${deferredDownForGenericEditor.paymentNumber}`;

    const updatedApp = produce(props.currentApp, updatedCurrentApp => {
      updatedCurrentApp.applicationLeaseQuote[amountFieldName] = deferredDownForGenericEditor.amount;
      updatedCurrentApp.applicationLeaseQuote[dateFieldName] = deferredDownForGenericEditor.paymentDate;

      consolidateDeferredDownPayments(updatedCurrentApp.applicationLeaseQuote);
    });
    dispatch(updateEditingApp(updatedApp, businessRulesEngine));
    closeDialog();
  }

  const openEditDeferredPaymentDialog = (deferredPayment: ILeaseQuoterDeferredDownForGenericEditor | undefined) => {
    const buttonText = deferredPayment ? "Update" : "Add";
    const showRemoveButton = !!deferredPayment;

    openDialog({
      children: (
        <GenericItemEditorDialog
          item={deferredPayment as ILeaseQuoterDeferredDownForGenericEditor}
          objectSpecificValidator={validateDeferredDownPayment}
          title={"Deferred Down"}
          editableFieldInfos={editableFieldInfos}
          submitButtonText={buttonText}
          onSubmit={(updatedDeferredPayment: ILeaseQuoterDeferredDownForGenericEditor) => handleUpdateDeferredPayment(updatedDeferredPayment)}
          closeDialog={closeDialog}
          onRemove={showRemoveButton ? handleRemoveSingleDeferredPayment : undefined}
        />
      )
    })
  }

  return (
    <div className={styles.leaseDeferredDownEditorContainer}>
      <Card className={props.className}>
        <CardHeader>
          <CardTitle>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'left' }}>
              <span>Deferred Down</span>
            </div>
          </CardTitle>
        </CardHeader>
        <GenericGrid<ILeaseQuoterDeferredDownForGenericEditor>
          style={{ display: 'flex', flex: 1 }}
          size={'small'}

          useIconButtons={true}
          columns={[
            { title: "Date", field: "paymentDate", width: 100, format: "{0:d}" },
            { title: "Amount", field: "amount", format: "{0:c}" }
          ]}
          itemName={' '}
          itemNameStyle={{ fontWeight: 'bold', fontSize: '1.2em' }}
          items={deferredPayments.map(payment => { return { ...payment, paymentDate: hasValue(payment.paymentDate) ? new Date(payment.paymentDate!) : undefined } })}
          disableAdding={true}
          disableEditing={props.leaseQuoteFieldIsDisabled("deferredPayments") || deferredPayments.length > maxNumberOfPayments}
          disableExcelExporting={true}
          disablePdfExporting={true}
          disableFiltering={true}
          disableGrouping={true}
          onOpenItemEditor={(deferredPayment: ILeaseQuoterDeferredDownForGenericEditor | undefined) => openEditDeferredPaymentDialog(deferredPayment)}
          onItemSelected={(deferredPayment: ILeaseQuoterDeferredDownForGenericEditor) => openEditDeferredPaymentDialog(deferredPayment)}
          disablePaging={deferredPayments.length <= 20}
        />
      </Card>
    </div>
  );
}
